import { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";

import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

import { isNumber } from "../../Utils/Functions";

const Interactor = (props: any) => {
  const [chain, setChain] = useState("");
  const [active, setActive] = useState("");
  const [passive, setPassive] = useState("");
  const [partner, setPartner] = useState("");

  const [activeInputValid, setActiveInputValid] = useState(true);
  const [passiveInputValid, setPassiveInputValid] = useState(true);
  const [partnerValid, setPartnerValid] = useState(true);
  const [chainValid, setChainValid] = useState(true);

  const [activeResnumValid, setActiveResnumValid] = useState(true);
  const [passiveResnumValid, setPassiveResnumValid] = useState(true);

  const [selfInteraction, setSelfInteraction] = useState(false);

  let errorMsg = "Only numbers and `,` are allowed.";
  let errorMsgActPas = "Residues cannot be both active and passive.";
  let errorMsgSelfInteraction = "Interactor cannot be its own partner.";
  let errorMsgChain = "Only alphanumeric characters are allowed.";

  // Check if the input contains only numbers or `,`
  const isValidInput = (str: string) => {
    let arr: boolean[] = [];
    for (let i = 0; i < str.length; i++) {
      let char = str[i];
      if (!isNumber(char) && char !== "," && char !== "") {
        arr.push(false);
      } else {
        arr.push(true);
      }
    }
    // returns true if all are true
    return arr.every((element) => element === true);
  };

  // Check if its interacting with itself
  const isSelfInteraction = (str: string) => {
    let arr: string[] = str.split(",");
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] === props.id) {
        return true;
      }
    }
    return false;
  };

  // Check if two string contain the same residue number
  const isBothActPas = (act: string, pas: string) => {
    let actarr = act.split(",").filter((e) => e);
    let pasarr = pas.split(",").filter((e) => e);
    for (var i = 0; i < actarr.length; i++) {
      let resI = actarr[i];
      for (var j = 0; j < pasarr.length; j++) {
        let resJ = pasarr[j];
        if (resI === resJ) {
          return true;
        }
      }
    }
    return false;
  };

  // The props.onChangeValue is a function defined on the parent component
  //  that lifts the state from this component
  const chainChangeHandler = (event: any) => {
    setChain(event.target.value);
    // Clear errors if empty
    if (!event.target.value) {
      setChainValid(true);
      props.error(false);
    }

    // Check if its alphanumeric
    if (event.target.value && !event.target.value.match(/^[0-9a-zA-Z]+$/)) {
      setChainValid(false);
      props.error(true);
    } else {
      setChainValid(true);
      props.error(false);
    }
    props.onChangeValue(props.id, "chain", event.target.value);
  };

  const activeChangeHandler = (event: any) => {
    setActive(event.target.value);
    // Clear errors if empty
    if (!event.target.value) {
      setActiveInputValid(true);
      setActiveResnumValid(true);
      props.error(false);
    }

    // Check if input is valid
    if (isValidInput(event.target.value)) {
      setActiveInputValid(true);
      props.error(false);
      // Check if active and passive contain the same residue number
      if (isBothActPas(event.target.value, passive)) {
        setActiveResnumValid(false);
        props.error(true);
      } else {
        setActiveResnumValid(true);
        setPassiveResnumValid(true);
        props.error(false);
      }
    } else {
      setActiveInputValid(false);
      props.error(true);
    }
    props.onChangeValue(props.id, "active", event.target.value);
  };

  const passiveChangeHandler = (event: any) => {
    setPassive(event.target.value);
    // Clear errors if empty
    if (!event.target.value) {
      setPassiveInputValid(true);
      setPassiveResnumValid(true);
      props.error(false);
    }

    // Check if input is valid
    if (isValidInput(event.target.value)) {
      setPassiveInputValid(true);
      props.error(false);
      // Check if active and passive contain the same residue number
      if (isBothActPas(event.target.value, active)) {
        setPassiveResnumValid(false);
        props.error(true);
      } else {
        setPassiveResnumValid(true);
        setActiveResnumValid(true);
        props.error(false);
      }
    } else {
      setPassiveInputValid(false);
      props.error(true);
    }
    props.onChangeValue(props.id, "passive", event.target.value);
  };

  const partnerChangeHandler = (event: any) => {
    setPartner(event.target.value);

    // Clear errors if empty
    if (!event.target.value) {
      setPartnerValid(true);
      props.error(false);
    }

    // Check if input is valid
    if (!isValidInput(event.target.value)) {
      setPartnerValid(false);
      props.error(true);
    } else {
      setPartnerValid(true);
      props.error(false);
    }

    // Check if its not interacting with itself
    if (isSelfInteraction(event.target.value)) {
      setSelfInteraction(true);
      props.error(true);
    } else {
      setSelfInteraction(false);
      props.error(false);
    }
    props.onChangeValue(props.id, "partner", event.target.value);
  };

  const handleRemoveClick = () => {
    props.onRemoveBtnPress(props.id);
  };

  return (
    <Card style={{ margin: "20px" }}>
      <CardContent>
        <Typography sx={{ fontSize: 18 }} color="text.secondary" gutterBottom>
          Interactor {props.id}
        </Typography>
        <Tooltip title="Chain Identifier" arrow>
          <TextField
            id="standard-basic"
            label="Chain"
            variant="standard"
            required
            style={{ padding: "5px" }}
            value={chain}
            error={!chainValid}
            helperText={!chainValid ? errorMsgChain : ""}
            inputProps={{ maxLength: 1 }}
            onChange={chainChangeHandler}
          />
        </Tooltip>

        <Tooltip
          title="Residues that MUST be involved in the interaction"
          arrow
        >
          <TextField
            id="standard-basic"
            label="Active"
            variant="standard"
            type="text"
            style={{ padding: "5px" }}
            value={active}
            error={!activeInputValid || !activeResnumValid}
            helperText={
              !activeInputValid
                ? errorMsg
                : !activeResnumValid
                ? errorMsgActPas
                : ""
            }
            onChange={activeChangeHandler}
          />
        </Tooltip>

        <Tooltip
          title="Residues that SHOULD be involved in the interaction"
          arrow
        >
          <TextField
            id="standard-basic"
            label="Passive"
            variant="standard"
            type="text"
            style={{ padding: "5px" }}
            value={passive}
            error={!passiveInputValid || !passiveResnumValid}
            helperText={
              !passiveInputValid
                ? errorMsg
                : !passiveResnumValid
                ? errorMsgActPas
                : ""
            }
            onChange={passiveChangeHandler}
          />
        </Tooltip>

        <Tooltip title="To which this interactor should be paired with" arrow>
          <TextField
            required
            label="Partner"
            variant="standard"
            id="standard-basic"
            type="text"
            style={{ padding: "5px" }}
            value={partner}
            error={!partnerValid || selfInteraction}
            helperText={
              !partnerValid
                ? errorMsg
                : selfInteraction
                ? errorMsgSelfInteraction
                : ""
            }
            onChange={partnerChangeHandler}
          />
        </Tooltip>

        <Tooltip title="Delete this interactor">
          <IconButton
            aria-label="delete"
            type="button"
            onClick={handleRemoveClick}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </CardContent>
    </Card>
  );
};

export default Interactor;
